import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import {
  ArrowLeft, ArrowRightLeft, CheckCircle2, Circle,
  AlertTriangle, Loader2, CheckCheck, ChevronRight,
} from 'lucide-react';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

interface ConfigMigracion {
  anioInicio: number;
  mesInicio: number;  // 1–12
}

type EstadoMigracion = 'pendiente' | 'migrando' | 'ok' | 'error';

interface ResultadoMigracion {
  estado: EstadoMigracion;
  error?: string;
}

export const MigracionPresupuestos: React.FC = () => {
  const navigate = useNavigate();
  const { presupuestos, categorias, migrarPresupuestoAAnual } = useData();

  const hoy = new Date();
  const anioActual = hoy.getFullYear();
  const mesActual  = hoy.getMonth() + 1; // 1-12

  // ── Datos derivados ───────────────────────────────────────────────────────

  // Modelo evergreen: hay un único documento por presupuesto mensual, así que ya no
  // hace falta deduplicar por nombre+categoría. La migración a anual es un update
  // in-place del mismo documento (su id no cambia).
  const presupuestosMensuales = useMemo(() =>
    presupuestos.filter(p => p.periodicidad === 'mensual' && !p.migradoDesdeMensual),
  [presupuestos]);

  const presupuestosAnuales = useMemo(() =>
    presupuestos.filter(p => p.periodicidad === 'anual'),
  [presupuestos]);

  const presupuestosMigrados = useMemo(() =>
    presupuestos.filter(p => p.migradoDesdeMensual),
  [presupuestos]);

  const getNombreCategoria = (id: string) => {
    const c = categorias.find(c => c.id === id);
    return c ? `${c.icono} ${c.nombre}` : 'Desconocida';
  };

  // ── Estado del formulario ─────────────────────────────────────────────────

  const [seleccionados, setSeleccionados] = useState<Set<string>>(new Set());
  const [configs, setConfigs]             = useState<Record<string, ConfigMigracion>>({});
  const [resultados, setResultados]       = useState<Record<string, ResultadoMigracion>>({});
  const [migrando, setMigrando]           = useState(false);
  const [paso, setPaso]                   = useState<'seleccion' | 'configurar' | 'resultado'>('seleccion');

  // ── Selección ─────────────────────────────────────────────────────────────

  const toggleSeleccion = (id: string) => {
    setSeleccionados(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); }
      else {
        next.add(id);
        // Config por defecto si no existe
        if (!configs[id]) {
          setConfigs(c => ({ ...c, [id]: { anioInicio: anioActual, mesInicio: mesActual } }));
        }
      }
      return next;
    });
  };

  const seleccionarTodos = () => {
    const todos = new Set(presupuestosMensuales.map(p => p.id));
    setSeleccionados(todos);
    const defaultConfigs: Record<string, ConfigMigracion> = {};
    presupuestosMensuales.forEach(p => {
      if (!configs[p.id]) defaultConfigs[p.id] = { anioInicio: anioActual, mesInicio: mesActual };
    });
    setConfigs(c => ({ ...c, ...defaultConfigs }));
  };

  const deseleccionarTodos = () => setSeleccionados(new Set());

  const setConfig = (id: string, campo: keyof ConfigMigracion, valor: number) => {
    setConfigs(c => ({ ...c, [id]: { ...(c[id] || { anioInicio: anioActual, mesInicio: mesActual }), [campo]: valor } }));
  };

  // ── Migración ─────────────────────────────────────────────────────────────

  const ejecutarMigracion = async () => {
    setMigrando(true);
    const ids = Array.from(seleccionados);
    const nuevosResultados: Record<string, ResultadoMigracion> = {};

    // Inicializar todos como "migrando"
    ids.forEach(id => { nuevosResultados[id] = { estado: 'migrando' }; });
    setResultados({ ...nuevosResultados });
    setPaso('resultado');

    for (const id of ids) {
      const cfg = configs[id] || { anioInicio: anioActual, mesInicio: mesActual };
      try {
        await migrarPresupuestoAAnual({
          presupuestoMensualId: id,
          anioInicio: cfg.anioInicio,
          mesInicio: cfg.mesInicio,
        });
        nuevosResultados[id] = { estado: 'ok' };
      } catch (err: any) {
        nuevosResultados[id] = { estado: 'error', error: err.message || 'Error desconocido' };
      }
      setResultados({ ...nuevosResultados });
    }

    setMigrando(false);
  };

  // ── Render helpers ────────────────────────────────────────────────────────

  const inputClass = "px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm";

  const totalOk    = Object.values(resultados).filter(r => r.estado === 'ok').length;
  const totalError = Object.values(resultados).filter(r => r.estado === 'error').length;
  const totalMig   = Object.keys(resultados).length;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">

        {/* Cabecera */}
        <div className="flex items-center gap-3 mb-8">
          <button onClick={() => navigate('/finanzas/presupuestos')}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Migración de Presupuestos</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">
              Convierte presupuestos mensuales a anuales con objetivo de seguimiento
            </p>
          </div>
        </div>

        {/* ── Dashboard de estado ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Mensuales activos', valor: presupuestosMensuales.length, color: 'blue' },
            { label: 'Anuales',           valor: presupuestosAnuales.length,   color: 'green' },
            { label: 'Migrados',          valor: presupuestosMigrados.length,  color: 'purple' },
            { label: 'Pendientes',        valor: presupuestosMensuales.length, color: 'amber' },
          ].map(({ label, valor, color }) => (
            <div key={label} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wide">{label}</p>
              <p className={`text-3xl font-bold mt-1 text-${color}-600 dark:text-${color}-400`}>{valor}</p>
            </div>
          ))}
        </div>

        {/* ── Sin mensuales pendientes ── */}
        {presupuestosMensuales.length === 0 && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
            <p className="font-semibold text-green-700 dark:text-green-300">No hay presupuestos mensuales pendientes de migrar</p>
            <p className="text-sm text-green-600 dark:text-green-400 mt-1">Todos los presupuestos ya están en formato anual.</p>
          </div>
        )}

        {/* ── Resultado de la migración ── */}
        {paso === 'resultado' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              {migrando
                ? <><Loader2 className="w-5 h-5 animate-spin text-blue-500" /> Migrando…</>
                : <><CheckCheck className="w-5 h-5 text-green-500" /> Resultado de la migración</>
              }
            </h2>

            {!migrando && (
              <div className="flex gap-4 mb-4">
                <div className="flex items-center gap-2 text-green-600 dark:text-green-400 font-semibold">
                  <CheckCircle2 className="w-4 h-4" /> {totalOk} migrados correctamente
                </div>
                {totalError > 0 && (
                  <div className="flex items-center gap-2 text-red-600 dark:text-red-400 font-semibold">
                    <AlertTriangle className="w-4 h-4" /> {totalError} con error
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              {Array.from(seleccionados).map(id => {
                const p = presupuestos.find(x => x.id === id);
                const res = resultados[id];
                if (!p || !res) return null;
                return (
                  <div key={id} className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                    res.estado === 'ok'       ? 'bg-green-50 dark:bg-green-900/20'
                    : res.estado === 'error'  ? 'bg-red-50 dark:bg-red-900/20'
                    : 'bg-gray-50 dark:bg-gray-700/50'
                  }`}>
                    {res.estado === 'migrando' && <Loader2 className="w-4 h-4 animate-spin text-blue-500 shrink-0" />}
                    {res.estado === 'ok'       && <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />}
                    {res.estado === 'error'    && <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />}
                    <span className="font-semibold text-gray-800 dark:text-gray-200 flex-1">{p.nombre}</span>
                    {res.estado === 'error' && (
                      <span className="text-red-600 dark:text-red-400 text-xs">{res.error}</span>
                    )}
                    {res.estado === 'ok' && (
                      <span className="text-green-600 dark:text-green-400 text-xs">
                        €{(p.limite * 12).toFixed(2)}/año
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {!migrando && (
              <div className="flex gap-3 mt-5">
                <button onClick={() => navigate('/finanzas/presupuestos')}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition">
                  Ver presupuestos anuales
                </button>
                {totalError > 0 && (
                  <button onClick={() => { setPaso('seleccion'); setResultados({}); }}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm">
                    Reintentar
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Selección y configuración ── */}
        {paso !== 'resultado' && presupuestosMensuales.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                Presupuestos mensuales disponibles
              </h2>
              <div className="flex gap-2">
                <button onClick={seleccionarTodos}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-semibold">
                  Seleccionar todos
                </button>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <button onClick={deseleccionarTodos}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:underline font-semibold">
                  Limpiar
                </button>
              </div>
            </div>

            <div className="space-y-3">
              {presupuestosMensuales.map(p => {
                const selec = seleccionados.has(p.id);
                const cfg   = configs[p.id] || { anioInicio: anioActual, mesInicio: mesActual };

                return (
                  <div key={p.id} className={`border-2 rounded-lg transition ${
                    selec
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30'
                  }`}>
                    {/* Fila principal — clic para seleccionar */}
                    <div className="flex items-center gap-3 p-4 cursor-pointer"
                      onClick={() => toggleSeleccion(p.id)}>
                      <div className={`w-5 h-5 shrink-0 flex items-center justify-center rounded-full border-2 transition ${
                        selec ? 'border-blue-500 bg-blue-500' : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        {selec && <CheckCircle2 className="w-4 h-4 text-white fill-white" />}
                        {!selec && <Circle className="w-4 h-4 text-transparent" />}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 dark:text-white truncate">{p.nombre}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {getNombreCategoria(p.categoriaId)}
                          {' · '}€{p.limite.toFixed(2)}/mes
                          {' → '}
                          <span className="text-blue-600 dark:text-blue-400 font-semibold">
                            €{(p.limite * 12).toFixed(2)}/año
                          </span>
                        </p>
                      </div>

                      <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${selec ? 'rotate-90' : ''}`} />
                    </div>

                    {/* Panel de configuración — solo si seleccionado */}
                    {selec && (
                      <div className="px-4 pb-4 pt-1 border-t border-blue-200 dark:border-blue-800 grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                            Año de inicio
                          </label>
                          <input type="number" inputMode="numeric" min="2020" max="2100"
                            value={cfg.anioInicio}
                            onChange={e => setConfig(p.id, 'anioInicio', parseInt(e.target.value, 10))}
                            className={inputClass + ' w-full'} />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                            Mes de inicio
                          </label>
                          <select value={cfg.mesInicio}
                            onChange={e => setConfig(p.id, 'mesInicio', parseInt(e.target.value, 10))}
                            className={inputClass + ' w-full'}>
                            {MESES.map((nombre, idx) => (
                              <option key={idx + 1} value={idx + 1}>{nombre}</option>
                            ))}
                          </select>
                        </div>
                        {cfg.mesInicio > 1 && (
                          <p className="col-span-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded px-2 py-1">
                            Se asumirá consumo teórico de €{((cfg.mesInicio - 1) * p.limite).toFixed(2)} para los {cfg.mesInicio - 1} meses previos ({MESES.slice(0, cfg.mesInicio - 1).join(', ')})
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Barra de acción */}
            {seleccionados.size > 0 && (
              <div className="mt-6 flex items-center justify-between gap-4 pt-5 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-bold text-gray-900 dark:text-white">{seleccionados.size}</span> presupuesto{seleccionados.size !== 1 ? 's' : ''} seleccionado{seleccionados.size !== 1 ? 's' : ''}
                  {' · '}
                  <span className="text-amber-600 dark:text-amber-400 font-semibold">Esta acción no se puede deshacer</span>
                </div>
                <button
                  onClick={ejecutarMigracion}
                  disabled={migrando}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition disabled:opacity-50 shrink-0">
                  {migrando
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Migrando…</>
                    : <><ArrowRightLeft className="w-4 h-4" /> Migrar a anual</>
                  }
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Presupuestos ya migrados ── */}
        {presupuestosMigrados.length > 0 && (
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-base font-bold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-500" /> Presupuestos ya migrados
            </h2>
            <div className="space-y-2">
              {presupuestosMigrados.map(p => (
                <div key={p.id} className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400 px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                  <span className="font-semibold text-gray-800 dark:text-gray-200 flex-1">{p.nombre}</span>
                  <span>{getNombreCategoria(p.categoriaId)}</span>
                  <span className="font-semibold text-blue-600 dark:text-blue-400">€{p.limite.toFixed(2)}/año</span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};
